import { useCallback, useEffect, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import "./onboarding.css";

const CARD_WIDTH = 336;

function saved(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "done";
  } catch {
    return false;
  }
}

export default function OnboardingTour({ steps, storageKey }) {
  const [open, setOpen] = useState(() => !saved(storageKey));
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const step = steps[stepIndex];

  const close = useCallback(() => {
    try {
      localStorage.setItem(storageKey, "done");
    } catch {}
    setOpen(false);
  }, [storageKey]);

  useEffect(() => {
    if (!open || !step) return;
    const target = document.querySelector(step.selector);
    if (!target) return;

    const updateRect = () => setTargetRect(target.getBoundingClientRect());
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = window.setTimeout(updateRect, 250);
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [open, step]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, close]);

  const start = useCallback(() => {
    setStepIndex(0);
    setTargetRect(null);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener("onboarding:start", start);
    return () => window.removeEventListener("onboarding:start", start);
  }, [start]);

  const cardStyle = targetRect
    ? {
        left: Math.max(16, Math.min(targetRect.left, window.innerWidth - CARD_WIDTH - 16)),
        top:
          targetRect.bottom + 244 > window.innerHeight
            ? Math.max(16, targetRect.top - 224)
            : targetRect.bottom + 16,
      }
    : undefined;

  return (
    <>
      {open && step && (
        <div className="onboarding" role="dialog" aria-modal="true" aria-label="Guía de la pantalla">
          <div className="onboarding__backdrop" onClick={close} />
          {targetRect && (
            <div
              className="onboarding__highlight"
              style={{
                left: targetRect.left - 6,
                top: targetRect.top - 6,
                width: targetRect.width + 12,
                height: targetRect.height + 12,
              }}
            />
          )}
          <section className="onboarding__card" style={cardStyle}>
            <button type="button" className="onboarding__close" onClick={close} aria-label="Cerrar guía" title="Cerrar guía">
              <FiX aria-hidden="true" />
            </button>
            <span className="onboarding__count">{stepIndex + 1} de {steps.length}</span>
            <h2>{step.title}</h2>
            <p>{step.description}</p>
            <div className="onboarding__actions">
              <div>
                {stepIndex > 0 && (
                  <button type="button" className="onboarding__icon" onClick={() => setStepIndex(stepIndex - 1)} aria-label="Paso anterior" title="Paso anterior">
                    <FiChevronLeft aria-hidden="true" />
                  </button>
                )}
                <button type="button" className="onboarding__icon onboarding__icon--primary" onClick={() => stepIndex === steps.length - 1 ? close() : setStepIndex(stepIndex + 1)} aria-label={stepIndex === steps.length - 1 ? "Finalizar guía" : "Siguiente paso"} title={stepIndex === steps.length - 1 ? "Finalizar guía" : "Siguiente paso"}>
                  <FiChevronRight aria-hidden="true" />
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
